import {
    IAttribute,
    IValidationContext,
    IViolation,
    LakehouseTableDataset,
    Metamodel,
    StoreSettingPartitionKey,
    sanitizeAndFormatString,
    stringIsNullOrEmpty,
    UpdateMethodType,
    UpdateMethod,
    ICopySinkWithUpdateMethod,
    format,
    ModelConstants
} from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { TabularCopySink } from './tabular.copysink';
import type { CopyActivity } from '../copy.activity';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { DeltaSinkSettings } from '../delta-sink-settings';
import { Owned } from 'app/model/base/named';
import { GlobalVariables } from 'app/services/globals';
import type { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';
import { getDisplayName } from 'app/model/pipeline/copysink/copysink.utils';
import { getCloneCompatibleUpsertKeyRequirement, isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';

export enum TableActionOption {
    Append = 'Append',
    Overwrite = 'Overwrite',
    OverwriteSchema = 'OverwriteSchema',
    Upsert = 'Upsert',
    SCD2 = 'SCD2'
}

export type TableActionOptionTypes = `${TableActionOption}`;

export const supportedSourceDatasetsForTimestampNtz = [
    adfModelTypeMap.delta.datasetType,
    adfModelTypeMap.parquet.datasetType,
    adfModelTypeMap.delimitedText.datasetType,
    adfModelTypeMap.xml.datasetType,
    adfModelTypeMap.json.datasetType,
    adfModelTypeMap.binary.datasetType,
    adfModelTypeMap.excel.datasetType,
    adfModelTypeMap.snowflakeV2.datasetType,
    adfModelTypeMap.oracle.datasetType,
    adfModelTypeMap.googleBigQueryV2.datasetType,
    adfModelTypeMap.mysql.datasetType,
    adfModelTypeMap.azureMySql.datasetType,
    adfModelTypeMap.azureSql.datasetType,
    adfModelTypeMap.azureSqlMI.datasetType,
    adfModelTypeMap.sqlServer.datasetType,
    adfModelTypeMap.azureSqlDW.datasetType,
    adfModelTypeMap.DataWarehouse.datasetType,
    adfModelTypeMap.fabricSql.datasetType,
    adfModelTypeMap.sqlAnalyticsEndpoint.datasetType,
    adfModelTypeMap.lakehouseTable.datasetType
];

@Metamodel.model({
    name: adfModelTypeMap.lakehouseTable.copySinkType,
    display: [],
    referable: true,
    tags: { datasetModel: Metamodel.modelFor(LakehouseTableDataset) },
    validator: LakehouseTableCopySink._validateTableMethods
})
export class LakehouseTableCopySink extends TabularCopySink implements ICopySinkWithUpdateMethod {
    public static readonly MAX_ROWS_PER_FILE: IAttribute;
    public static readonly PARTITION_OPTION: IAttribute;
    public static readonly APPEND_PARTITION_OPTION: IAttribute;
    public static readonly TABLE_ACTION: IAttribute;
    public static readonly PARTITION_KEYS: IAttribute;
    public static readonly APPEND_PARTITION_KEYS: IAttribute;
    public static readonly VERTI_PARQUET: IAttribute;
    public static readonly KEY_COLUMNS: IAttribute;
    public static readonly DELTA_SINK_SETTINGS: IAttribute;
    public static readonly APPLY_VORDER: IAttribute;
    public static readonly UPSERT_PARTITION_OPTION: IAttribute;
    public static readonly UPSERT_PARTITION_KEYS: IAttribute;
    public static readonly ENABLE_TIMESTAMP_NTZ: IAttribute;
    public static readonly ENABLE_CHANGE_DATA_FEED: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'MAX_ROWS_PER_FILE',
        accepts: ['Int'],
        display: stringResources.ModelName_MaxRowsPerFileLabel
    })
    public maxRowsPerFile?: number;

    @Metamodel.attribute({
        association: 'PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enablePartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public partitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        association: 'APPEND_PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enableAppendPartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'APPEND_PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public appendPartitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        association: 'UPSERT_PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enableUpsertPartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'UPSERT_PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public upsertPartitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        display: stringResources.TableAction,
        association: 'TABLE_ACTION',
        templatable: true,
        accepts: ['String']
    })
    public tableActionOption?: TableActionOptionTypes = TableActionOption.Append;

    @Metamodel.attribute({
        association: 'VERTI_PARQUET',
        templatable: true
    })
    public vertiParquet: boolean = __IS_FABRIC_DPE__ ? true : undefined;

    @Metamodel.attribute({
        display: stringResources.DataFlowDatasetKeyColumns,
        association: 'KEY_COLUMNS',
        templatable: true,
        accepts: ['Array']
    })
    public keyColumns?: string[];

    @Metamodel.attribute({
        display: stringResources.DeltaSinkSettings,
        association: 'DELTA_SINK_SETTINGS',
        templatable: false,
        type: DeltaSinkSettings.MODEL
    })
    public deltaSinkSettings?: DeltaSinkSettings = __IS_FABRIC_DPE__
        ? Owned.create(DeltaSinkSettings.MODEL, this)
        : undefined;

    @Metamodel.attribute({
        display: stringResources.Dataset_applyVOrder,
        association: 'APPLY_VORDER',
        templatable: true
    })
    public applyVOrder?: boolean = __IS_FABRIC_DPE__ ? false : undefined;

    @Metamodel.attribute({
        display: stringResources.Dataset_enableTimestampNtz,
        association: 'ENABLE_TIMESTAMP_NTZ',
        templatable: true
    })
    public enableTimestampNtz?: boolean = __IS_FABRIC_DPE__ && GlobalVariables.timestampNtz ? false : undefined;

    // use this flag to mark the entity whose overwrite is the old value: "Overwrite"
    public isOldResource: boolean = false;

    public get isTimestampNtzSupported(): boolean {
        const sourceDataset = (this.owner as CopyActivity)?.sourceDataset;
        return __IS_FABRIC_DPE__ && GlobalVariables.timestampNtz &&
            sourceDataset != null &&
            supportedSourceDatasetsForTimestampNtz.includes(Metamodel.modelOf(sourceDataset).name);
    }

    @Metamodel.attribute({
        association: 'ENABLE_CHANGE_DATA_FEED',
        display: stringResources.CopyWizard_Summary_EnableCDFCopy
    })
    public enableChangeDataFeed?: boolean;

    public get updateMethod(): UpdateMethodType {
        switch (this.tableActionOption) {
            case TableActionOption.Append:
                return UpdateMethod.Append;
            case TableActionOption.Overwrite:
            case TableActionOption.OverwriteSchema:
                return UpdateMethod.Overwrite;
            case TableActionOption.Upsert:
                return UpdateMethod.Upsert;
            case TableActionOption.SCD2:
                return UpdateMethod.SCD2;
            default:
                return undefined;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Append:
                this.tableActionOption = TableActionOption.Append;
                break;
            case UpdateMethod.Overwrite:
                this.tableActionOption = this.isOldResource
                    ? TableActionOption.Overwrite
                    : TableActionOption.OverwriteSchema;
                break;
            case UpdateMethod.Upsert:
                this.tableActionOption = TableActionOption.Upsert;
                break;
            case UpdateMethod.SCD2:
                this.tableActionOption = TableActionOption.SCD2;
                break;
            default:
        }
    }

    public get upsertKeyColumns(): string[] {
        return this.keyColumns;
    }

    public set upsertKeyColumns(value: string[]) {
        this.keyColumns = value;
    }

    public static validateKeyColumns(
        sink: LakehouseTableCopySink,
        upsertLabel?: string
    ): IViolation[] | undefined {
        if (isDeltaCloneCopy(sink.owner as CopyActivity)) {
            return [];
        }
        if (upsertLabel == null) {
            if (sink.owner && Metamodel.modelOf(sink.owner)?.name === ModelConstants.copyJobActivity) {
                upsertLabel =
                    (sink.owner as CopyJobActivity).updateMethod === UpdateMethod.SCD2
                        ? stringResources.WriteBehavior_Option_SCD2
                        : stringResources.MergeLabel;
            } else {
                upsertLabel = stringResources.WriteBehavior_Option_Upsert;
            }
        }
        const activityName = getDisplayName(sink);
        if (
            (sink.tableActionOption !== 'Upsert' && sink.updateMethod !== UpdateMethod.SCD2) ||
            !stringIsNullOrEmpty(LakehouseTableCopySink.KEY_COLUMNS.templateValue(sink))
        ) {
            return [];
        } else if (!sink.keyColumns || sink.keyColumns?.length === 0) {
            const activity = sink.owner as CopyJobActivity;
            const required = (Metamodel.modelOf(activity)?.name === ModelConstants.copyJobActivity && sink.updateMethod === UpdateMethod.Upsert
                ? getCloneCompatibleUpsertKeyRequirement(activity) : undefined) ?? true;
            if (!required) {
                return [];
            }
            return [
                {
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, activityName),
                    redirectTo: {
                        attribute: LakehouseTableCopySink.KEY_COLUMNS,
                        entity: sink
                    }
                }
            ];
        }
        const violations: IViolation[] = [];
        const uniqueNames: Set<string> = new Set();
        let hasEmptyString = false;
        sink.keyColumns.forEach((key) => {
            if (key === '') {
                if (!hasEmptyString) {
                    violations.push({
                        context: sink,
                        message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, activityName),
                        redirectTo: {
                            attribute: LakehouseTableCopySink.KEY_COLUMNS,
                            entity: sink
                        }
                    });
                    hasEmptyString = true;
                }
            } else if (uniqueNames.has(key)) {
                violations.push({
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.DupKeyColumn, key),
                    redirectTo: {
                        attribute: LakehouseTableCopySink.KEY_COLUMNS,
                        entity: sink
                    }
                });
            } else {
                uniqueNames.add(key);
            }
        });
        return violations;
    }

    private static async _validateTableMethods(
        activity: LakehouseTableCopySink,
        context: IValidationContext
    ): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];
        if (__IS_FABRIC_DPE__) {
            violations.push(...(LakehouseTableCopySink.validateKeyColumns(activity) || []));
        }
        violations.push(...(LakehouseTableCopySink._validatePartitionNames(activity) || []));

        return violations;
    }

    private static _validatePartitionNames(lhTableCopySink: LakehouseTableCopySink): IViolation[] | undefined {
        const nameSet = new Set<string>();
        const tableActionOption = ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.TABLE_ACTION);
        const hasPartitionOption =
            tableActionOption === 'Append' ||
            tableActionOption === 'Overwrite' ||
            tableActionOption === 'OverwriteSchema' ||
            (
                tableActionOption === 'Upsert' &&
                __IS_FABRIC_DPE__ &&
                GlobalVariables.lakehouseUpsertPartition
            )
        isExpressionObject(tableActionOption);
        const enablePartitionOption =
            tableActionOption === 'Append'
                ? ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.APPEND_PARTITION_OPTION)
                : tableActionOption === 'Upsert' && __IS_FABRIC_DPE__ && GlobalVariables.lakehouseUpsertPartition
                    ? ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.UPSERT_PARTITION_OPTION)
                    : ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.PARTITION_OPTION);
        const partitionNameList =
            tableActionOption === 'Append'
                ? ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.APPEND_PARTITION_KEYS)
                : tableActionOption === 'Upsert' && __IS_FABRIC_DPE__ && GlobalVariables.lakehouseUpsertPartition
                    ? ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.UPSERT_PARTITION_KEYS)
                    : ValueAdapter.toResource(lhTableCopySink, LakehouseTableCopySink.PARTITION_KEYS);

        if (hasPartitionOption && enablePartitionOption && Array.isArray(partitionNameList)) {
            // if enablePartitionOption is true, then partitionNameList must exist
            if (enablePartitionOption === true && arrayIsNullOrEmpty(partitionNameList)) {
                return [
                    {
                        context: lhTableCopySink.partitionNameList,
                        message: () => stringResources.Partition_Name_Not_Allow_EmptyName
                    }
                ];
            }
            // partitionNameList cannot contain empty values, as long as enablePartitionOption is not false
            for (const partitionName of partitionNameList) {
                const templated = ValueAdapter.isTemplated(partitionName, StoreSettingPartitionKey.PARTITITON_KEY);
                const value = templated
                    ? ValueAdapter.toResource(partitionName, StoreSettingPartitionKey.PARTITITON_KEY).value
                    : partitionName.partitionKey;
                if (!value) {
                    return [
                        {
                            context: lhTableCopySink.partitionNameList,
                            message: () => stringResources.Partition_Name_Not_Allow_EmptyName
                        }
                    ];
                }

                if (nameSet.has(value)) {
                    return [
                        {
                            context: lhTableCopySink.partitionNameList,
                            message: () => stringResources.Partition_Name_Not_Allow_DuplicateName
                        }
                    ];
                }
                nameSet.add(value);
            }

            if (tableActionOption === 'Upsert' && __IS_FABRIC_DPE__ && GlobalVariables.lakehouseUpsertPartition && lhTableCopySink.keyColumns?.length > 0) {
                const overlappedKeys = partitionNameList?.filter((partitionName) =>
                    lhTableCopySink.keyColumns.includes(partitionName.partitionKey)
                )?.map((item) => item.partitionKey);

                if (overlappedKeys?.length > 0) {
                    return [
                        {
                            context: lhTableCopySink.partitionNameList,
                            message: () => format(stringResources.Partition_Name_Cannot_Be_In_Key_Columns, stringResources.Upsert_Method, overlappedKeys.join(', '))
                        }
                    ];
                }
            }
        }
        const copyActivity = lhTableCopySink.owner as CopyActivity;

        if (nameSet.size > 0 && nameSet.size === copyActivity.cacheSourceSchema?.length) {
            return [
                {
                    context: lhTableCopySink.partitionNameList,
                    message: () => stringResources.Partition_Names_Not_Allow_All_Selected
                }
            ];
        }
        return undefined;
    }
}
